import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, NavLink, Outlet, Navigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RoleGuard from '../../components/guards/RoleGuard';
import AgencyTab from './AgencyTab';
import UsersTab from './UsersTab';
import BillingTab from './BillingTab';
import CalendarTab from './CalendarTab';
import SequencesTab from './SequencesTab';
import ExclusivityTab from './ExclusivityTab';
import TransfersTab from './TransfersTab';
import AiCallerTab from './AiCallerTab';
import LicensingResourcesTab from './LicensingResourcesTab';
import Spinner from '../../components/ui/Spinner';
import { api } from '../../lib/api';

const {
  FiSettings,
  FiUsers,
  FiCreditCard,
  FiGlobe,
  FiCalendar,
  FiMail,
  FiShield,
  FiZap,
  FiRepeat,
  FiClipboard,
} = FiIcons;

const MANAGEMENT_ROLES = ['admin', 'manager', 'super_admin'];
const TRANSFER_ADMIN_ROLES = ['admin', 'super_admin'];

function SettingsIndexRedirect() {
  const { user } = useAuth();
  const canManage = MANAGEMENT_ROLES.includes(user?.role);
  return <Navigate to={canManage ? '/settings/agency' : '/settings/calendar'} replace />;
}

function SettingsShell() {
  const { user } = useAuth();
  const canManage = MANAGEMENT_ROLES.includes(user?.role);
  const isTransferAdmin = TRANSFER_ADMIN_ROLES.includes(user?.role);
  const [pendingExclusivityCount, setPendingExclusivityCount] = useState(0);
  const [pendingTransferCount, setPendingTransferCount] = useState(0);
  const [showTransfersTab, setShowTransfersTab] = useState(isTransferAdmin);

  const refreshPendingExclusivityCount = useCallback(async () => {
    if (!canManage || !user?.agency_id) {
      setPendingExclusivityCount(0);
      return;
    }
    try {
      const count = await api.exclusivity.getPendingRequestCount(user.agency_id);
      setPendingExclusivityCount(count);
    } catch {
      setPendingExclusivityCount(0);
    }
  }, [canManage, user?.agency_id]);

  useEffect(() => {
    void refreshPendingExclusivityCount();
  }, [refreshPendingExclusivityCount]);

  const refreshPendingTransferCount = useCallback(async () => {
    if (!user?.agency_id) {
      setPendingTransferCount(0);
      if (!isTransferAdmin) setShowTransfersTab(false);
      return;
    }
    try {
      const count = await api.ownershipTransfers.getPendingActionCount(user.agency_id);
      setPendingTransferCount(count);
      if (!isTransferAdmin) setShowTransfersTab(count > 0);
    } catch {
      setPendingTransferCount(0);
      if (!isTransferAdmin) setShowTransfersTab(false);
    }
  }, [user?.agency_id, isTransferAdmin]);

  useEffect(() => {
    void refreshPendingTransferCount();
    const onTransferChange = () => { void refreshPendingTransferCount(); };
    window.addEventListener('transfer-pending-change', onTransferChange);
    return () => window.removeEventListener('transfer-pending-change', onTransferChange);
  }, [refreshPendingTransferCount]);

  const tabClass = ({ isActive }) =>
    `flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
      isActive
        ? 'border-primary text-primary'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">
          <SafeIcon icon={FiSettings} className="h-6 w-6 text-gray-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 capitalize">
            Role: <span className="font-semibold text-primary">{user?.role}</span>
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200 bg-white rounded-t-lg overflow-x-auto">
        <nav className="flex gap-0 min-w-max md:min-w-0">
          {canManage && (
            <>
              <NavLink to="/settings/agency" className={tabClass}>
                <SafeIcon icon={FiGlobe} className="w-4 h-4" />
                Agency
              </NavLink>
              <NavLink to="/settings/users" className={tabClass}>
                <SafeIcon icon={FiUsers} className="w-4 h-4" />
                Users
              </NavLink>
              <NavLink to="/settings/billing" className={tabClass}>
                <SafeIcon icon={FiCreditCard} className="w-4 h-4" />
                Billing
              </NavLink>
              <NavLink to="/settings/sequences" className={tabClass}>
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                Sequences
              </NavLink>
              <NavLink to="/settings/exclusivity" className={tabClass}>
                <SafeIcon icon={FiShield} className="w-4 h-4" />
                Exclusivity
                {pendingExclusivityCount > 0 && (
                  <span className="ml-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                    {pendingExclusivityCount > 99 ? '99+' : pendingExclusivityCount}
                  </span>
                )}
              </NavLink>
              {showTransfersTab && (
                <NavLink to="/settings/transfers" className={tabClass}>
                  <SafeIcon icon={FiRepeat} className="w-4 h-4" />
                  Transfers
                  {pendingTransferCount > 0 && (
                    <span className="ml-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                      {pendingTransferCount > 99 ? '99+' : pendingTransferCount}
                    </span>
                  )}
                </NavLink>
              )}
              <NavLink to="/settings/ai-caller" className={tabClass}>
                <SafeIcon icon={FiZap} className="w-4 h-4" />
                AI Caller
              </NavLink>
              <NavLink to="/settings/licensing" className={tabClass}>
                <SafeIcon icon={FiClipboard} className="w-4 h-4" />
                Licensing
              </NavLink>
            </>
          )}
          {!canManage && showTransfersTab && (
            <NavLink to="/settings/transfers" className={tabClass}>
              <SafeIcon icon={FiRepeat} className="w-4 h-4" />
              Transfers
              {pendingTransferCount > 0 && (
                <span className="ml-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                  {pendingTransferCount > 99 ? '99+' : pendingTransferCount}
                </span>
              )}
            </NavLink>
          )}
          <NavLink to="/settings/calendar" className={tabClass}>
            <SafeIcon icon={FiCalendar} className="w-4 h-4" />
            Calendar
          </NavLink>
        </nav>
      </div>

      <Outlet
        context={{
          setPendingExclusivityCount,
          refreshPendingExclusivityCount,
          setPendingTransferCount,
          refreshPendingTransferCount,
        }}
      />
    </div>
  );
}

function ExclusivitySettingsPage() {
  const { setPendingExclusivityCount, refreshPendingExclusivityCount } =
    useOutletContext() || {};
  return (
    <ExclusivityTab
      onPendingCountChange={(count) => {
        setPendingExclusivityCount?.(count);
        void refreshPendingExclusivityCount?.();
      }}
    />
  );
}

function TransfersAccessGuard({ children }) {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState(
    TRANSFER_ADMIN_ROLES.includes(user?.role) ? true : null,
  );

  useEffect(() => {
    if (TRANSFER_ADMIN_ROLES.includes(user?.role)) {
      setAllowed(true);
      return;
    }
    let cancelled = false;
    api.ownershipTransfers
      .getPendingActionCount(user?.agency_id)
      .then((count) => {
        if (!cancelled) setAllowed(count > 0);
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => { cancelled = true; };
  }, [user?.role, user?.agency_id]);

  if (allowed === null) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading…
      </div>
    );
  }
  if (!allowed) return <Navigate to="/settings/calendar" replace />;
  return children;
}

function TransfersSettingsPage() {
  const { setPendingTransferCount, refreshPendingTransferCount } =
    useOutletContext() || {};
  return (
    <TransfersTab
      onPendingCountChange={(count) => {
        setPendingTransferCount?.(count);
        void refreshPendingTransferCount?.();
      }}
    />
  );
}

/**
 * Nested routes for `/settings/*` — mount from App.jsx as path="/settings/*".
 */
export default function Settings() {
  return (
    <Routes>
      <Route element={<SettingsShell />}>
        <Route index element={<SettingsIndexRedirect />} />
        <Route
          path="agency"
          element={
            <RoleGuard allowedRoles={MANAGEMENT_ROLES} fallback="/settings/calendar">
              <AgencyTab />
            </RoleGuard>
          }
        />
        <Route
          path="users"
          element={
            <RoleGuard allowedRoles={MANAGEMENT_ROLES} fallback="/settings/calendar">
              <UsersTab />
            </RoleGuard>
          }
        />
        <Route
          path="billing"
          element={
            <RoleGuard allowedRoles={MANAGEMENT_ROLES} fallback="/settings/calendar">
              <BillingTab />
            </RoleGuard>
          }
        />
        <Route
          path="sequences"
          element={
            <RoleGuard allowedRoles={MANAGEMENT_ROLES} fallback="/settings/calendar">
              <SequencesTab />
            </RoleGuard>
          }
        />
        <Route
          path="exclusivity"
          element={
            <RoleGuard allowedRoles={MANAGEMENT_ROLES} fallback="/settings/calendar">
              <ExclusivitySettingsPage />
            </RoleGuard>
          }
        />
        <Route
          path="transfers"
          element={
            <TransfersAccessGuard>
              <TransfersSettingsPage />
            </TransfersAccessGuard>
          }
        />
        <Route path="calendar" element={<CalendarTab />} />
        <Route
          path="ai-caller"
          element={
            <RoleGuard allowedRoles={MANAGEMENT_ROLES} fallback="/settings/calendar">
              <AiCallerTab />
            </RoleGuard>
          }
        />
        <Route
          path="licensing"
          element={
            <RoleGuard allowedRoles={MANAGEMENT_ROLES} fallback="/settings/calendar">
              <LicensingResourcesTab />
            </RoleGuard>
          }
        />
      </Route>
    </Routes>
  );
}
